import { useMutation } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useTRPC } from '~/services/trpc'
import { UserOptions } from '~/stores/stores.types'
import { ParsedOptions } from '~/stores/types/settings'

import { HOMEPAGE_VIEW_ANALYTICS, HomepageView } from '@hap/constants'

import useCurrentUser from './useCurrentUser'

export function useCurrentUserSetting<
  Model extends keyof ParsedOptions,
  Setting extends keyof ParsedOptions[Model],
  SettingValue extends ParsedOptions[Model][Setting],
>(
  model: Model,
  settingKey: Setting,
  defaultValue: SettingValue,
): [SettingValue, (updatedValue: SettingValue) => void] {
  const trpc = useTRPC()
  const currentUser = useCurrentUser()
  const queryClient = useQueryClient()
  const { mutateAsync: update } = useMutation(
    trpc.users.update.mutationOptions({
      onMutate: async (updatedSettings) => {
        await queryClient.cancelQueries(trpc.users.getCurrentUser.pathFilter())
        const previousCurrentUser = queryClient.getQueryData(
          trpc.users.getCurrentUser.queryKey(),
        )
        queryClient.setQueryData(trpc.users.getCurrentUser.queryKey(), (prev) => {
          if (!prev) return prev
          return {
            ...prev,
            options: updatedSettings.values.options,
          }
        })
        return { previousCurrentUser }
      },
      onSettled() {
        queryClient.invalidateQueries(trpc.users.getCurrentUser.pathFilter())
      },
    }),
  )

  const hasOption = useCallback((value: unknown): value is SettingValue => !!value, [])
  const parsedModelOption: ParsedOptions[Model] = useMemo(() => {
    if (currentUser?.options) {
      const option = currentUser.options[model]
      if (option) {
        return JSON.parse(atob(option))
      }
    }
    return {}
  }, [currentUser, model])
  const settingValue = parsedModelOption[settingKey]

  const setUserOption = useCallback(
    async (updatedValue: SettingValue) => {
      if (currentUser) {
        const options: UserOptions = currentUser.options || {}
        await update({
          userId: currentUser.id,
          values: {
            options: {
              ...options,
              [model]: btoa(
                JSON.stringify({
                  ...parsedModelOption,
                  [settingKey]: updatedValue,
                }),
              ),
            },
          },
        })
      }
    },
    [currentUser, update, model, parsedModelOption, settingKey],
  )

  return [hasOption(settingValue) ? settingValue : defaultValue, setUserOption]
}

export function useUserHomepageView(): [
  HomepageView,
  (updatedValue: HomepageView) => void,
] {
  const currentUser = useCurrentUser()
  const [view, setView] = useCurrentUserSetting(
    'homepage',
    'view',
    currentUser?.roleHomepageDefault || HOMEPAGE_VIEW_ANALYTICS,
  )

  return [view, setView]
}
